import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';

import BrowseButton from 'Components/Utility/BrowseButton';
import ProductCarousel from 'Components/HomePage/ProductCarousel';
import HitCard from 'Components/Cards/HitCard';
import ItemList from 'Components/Product/Seo/ItemList';
import SkeletonCarousel from 'Components/Utility/Skeleton/SkeletonCarousel';

export default function ProductCarouselSection({
  fetchHits,
  headingTag,
  hitCardNameTag,
  hitsToDisplay,
  itemListTitle,
  lgSize,
  loadedHits,
  sectionCss,
  showHeader,
  slidesToShow,
  slugsToExclude,
  title,
  viewAllLocation,
  viewAllTitle,
  carouselCoverSize,
  lightThemeOverride,
  children,
  seoBlurb,
}) {
  const [hits, setHits] = useState([]);

  useEffect(() => {
    const doFetchHits = async () => {
      if (loadedHits) {
        setHits(loadedHits.slice(0, hitsToDisplay));
      } else if (fetchHits && typeof fetchHits === 'function') {
        const newHits = await fetchHits(hitsToDisplay, slugsToExclude);
        setHits(newHits);
      }
    };

    doFetchHits();
  }, [loadedHits]); // eslint-disable-line react-hooks/exhaustive-deps
  if (isEmpty(loadedHits) && !fetchHits) return false;

  const HeadingTag = headingTag;

  const sliderSettings = {
    lg: {
      slidesToShow,
      slidesToScroll: slidesToShow,
    },
    xl: {
      slidesToShow,
      slidesToScroll: slidesToShow,
    },
    xxl: {
      slidesToShow,
      slidesToScroll: slidesToShow,
    },
  };

  return (
    <section className={`ProductCarouselSection container ${sectionCss}`}>
      {showHeader &&
        <div className="section-heading-container">
          <HeadingTag className="section-title">{title}</HeadingTag>
          {viewAllLocation && <BrowseButton className={'view-all-btn'} location={viewAllLocation} />}
        </div>
      }
      {seoBlurb &&
        <div className="seo-blurb-text  mt-n3 mb-3">{seoBlurb}</div>
      }
      {isEmpty(hits) &&
        <div>
          <SkeletonCarousel slidesToShow={slidesToShow} />
        </div>
      }
      {!isEmpty(hits) && hits.length < slidesToShow &&
        <>
          <ItemList title={itemListTitle || title} hits={hits} />
          <div className="recommendation-container ProductCarouselSection__cardRow row">
            {hits && hits.slice(0, slidesToShow).map(hit =>
              <HitCard
                key={hit.product_id + Math.random()}
                hit={hit}
                lgSize={lgSize}
                mdSize={6}
                smSize={6}
                hitCardNameTag={hitCardNameTag}
                coverTypeOverride={carouselCoverSize}
                hitCardLightTheme={lightThemeOverride}
                adaptiveSize={carouselCoverSize === 'portrait' || carouselCoverSize === 'square'}
              />,
            )}
          </div>
        </>
      }
      {!isEmpty(hits) && hits.length >= slidesToShow &&
        <div className="ProductCarouselSection__carouselContainer">
          <ItemList title={title} hits={hits} />
          <ProductCarousel
            hitData={hits}
            slidesToShow={slidesToShow}
            viewAllLocation={viewAllLocation}
            title={title}
            viewAllTitle={viewAllTitle}
            hitCardNameTag={hitCardNameTag}
            carouselCoverSize={carouselCoverSize}
            hitCardLightTheme={lightThemeOverride}
            sliderSettings={sliderSettings}
          />
        </div>
      }
      {!!children &&
        <React.Fragment>
          {children}
        </React.Fragment>
      }
    </section>
  );
}

ProductCarouselSection.propTypes = {
  fetchHits: PropTypes.func,
  headingTag: PropTypes.string,
  hitCardNameTag: PropTypes.string,
  hitsToDisplay: PropTypes.number,
  itemListTitle: PropTypes.string,
  lgSize: PropTypes.number,
  loadedHits: PropTypes.arrayOf(PropTypes.shape({})),
  sectionCss: PropTypes.string,
  showHeader: PropTypes.bool,
  slidesToShow: PropTypes.number,
  slugsToExclude: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string,
  viewAllLocation: PropTypes.string,
  viewAllTitle: PropTypes.string,
  carouselCoverSize: PropTypes.oneOf([
    '',
    '16x9',
    'portrait',
    'square',
  ]),
  lightThemeOverride: PropTypes.bool,
};

ProductCarouselSection.defaultProps = {
  fetchHits: null,
  headingTag: 'h2',
  hitCardNameTag: 'div',
  hitsToDisplay: 12,
  itemListTitle: '',
  lgSize: 3,
  loadedHits: null,
  sectionCss: '',
  showHeader: true,
  slidesToShow: 4,
  slugsToExclude: [],
  title: '',
  viewAllLocation: '',
  viewAllTitle: '',
  carouselCoverSize: '',
  lightThemeOverride: false,
};
